import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { User } from 'shared/types';
import {
  isAuthorizedUser,
  isInternalUser,
  isLocationEditor,
} from 'shared/utils';
import {
  GeoEventTypePermissions,
  PermissionLevel,
  PermissionsData,
} from './usePermissions.types';
import { EDIT_PERMISSION_LEVELS } from './usePermissions.constants';
import { GeoEventTypes } from '../../constants';

export const isPermissionsQueryEnabled = ({
  geoEventType,
  user,
}: {
  geoEventType: GeoEventType | undefined;
  user: User | null;
}): boolean => {
  if (
    geoEventType === GeoEventTypes.wildfire ||
    geoEventType === GeoEventTypes.flooding
  ) {
    return isAuthorizedUser(user) || isInternalUser(user);
  }
  if (geoEventType === GeoEventTypes.location) {
    return (
      isAuthorizedUser(user) || isInternalUser(user) || isLocationEditor(user)
    );
  }
  return false;
};

// location carries a different permission shape so the report/geoEvent accessors below only apply to the other types.
const getGeoEventTypePermissions = (
  geoEventType: GeoEventType,
  permissionsData: PermissionsData | undefined,
): GeoEventTypePermissions | undefined =>
  geoEventType === GeoEventTypes.location
    ? undefined
    : permissionsData?.[geoEventType];

/**
 * Returns true when a permission level is known and disallows editing.
 * Returns false when undefined (query not loaded) so we don't
 * flash a disabled state before we know the answer.
 */
export const isEditDisabledForPermission = (
  editPermission: PermissionLevel | undefined,
): boolean =>
  editPermission != null && !EDIT_PERMISSION_LEVELS.includes(editPermission);

export const isEditReportDisabled = (
  geoEventType: GeoEventType,
  permissionsData: PermissionsData | undefined,
): boolean =>
  isEditDisabledForPermission(
    getGeoEventTypePermissions(geoEventType, permissionsData)?.report
      .editPermission,
  );

export const isEditGeoEventDisabled = (
  geoEventType: GeoEventType,
  permissionsData: PermissionsData | undefined,
): boolean =>
  isEditDisabledForPermission(
    geoEventType === GeoEventTypes.location
      ? permissionsData?.location?.editPermission
      : getGeoEventTypePermissions(geoEventType, permissionsData)?.geoEvent
          .editPermission,
  );

export const isCreateReportDisabled = (
  geoEventType: GeoEventType,
  permissionsData: PermissionsData | undefined,
): boolean =>
  getGeoEventTypePermissions(geoEventType, permissionsData)?.report
    .createPermission === 'none';

export const isCreateLocationDisabled = (
  permissionsData?: PermissionsData,
): boolean => permissionsData?.location?.createPermission === 'none';

export const isCreateLocationProvisional = (
  permissionsData?: PermissionsData,
): boolean => permissionsData?.location?.createPermission === 'provisional';

export const isEditLocationProvisional = (
  permissionsData?: PermissionsData,
): boolean => permissionsData?.location?.editPermission === 'provisional';

export const canReadReporterNotes = (
  permissionsData?: PermissionsData,
): boolean => permissionsData?.reporterNote?.readPermission === 'full';
